import { useState, useCallback } from "react";
import { pdf } from "@react-pdf/renderer";
import { downloadLocalBlob } from "infra/helpers/download";
import { useLoading } from "ui/components/GlobalLoading";
import { showError } from "infra/helpers/alerta";
import PDFMerger from "pdf-merger-js/browser";
import axios from "axios";
import { logError } from "infra/packages/@sentry/logError";
import { addPdfToMergerWithFallback } from "infra/helpers/rasterizeEncryptedPdf";

interface UseDownloadRenderedPdfProps<ComponentProps> {
  Component: (props: ComponentProps) => JSX.Element;
  fileName: string;
  loading?: boolean;
  loadingMessage?: string;
}

function useDownloadRenderedPdf<ComponentProps>({
  Component,
  fileName,
  loading,
  loadingMessage,
}: UseDownloadRenderedPdfProps<ComponentProps>) {
  const [_loading, setLoading] = useState(false);
  const [_loadingMessage, setLoadingMessage] = useState<string | undefined>();

  const handleAttachments = useCallback(
    async (originalBlob: Blob, pdfAttachments: string[]) => {
      const merger = new PDFMerger();
      await merger.add(originalBlob);
      for (let i = 0; i < pdfAttachments.length; i++) {
        const attachment = pdfAttachments[i];
        setLoadingMessage(
          `Anexando arquivos: ${i + 1} de ${pdfAttachments.length}`
        );
        const { data } = await axios({
          method: "get",
          url: attachment,
          responseType: "blob",
        });

        try {
          // merge direto; fallback de rasterização (pdf.js) se o pdf-lib não
          // conseguir ler o PDF (ex.: criptografado).
          await addPdfToMergerWithFallback(merger, data);
        } catch (err) {
          // um anexo problemático não deve abortar a exportação inteira
          logError(err);
        }
      }

      return await merger.saveAsBlob();
    },
    []
  );

  const onGetBlob = useCallback(
    async (componentProps: ComponentProps, pdfAttachments?: string[]) => {
      try {
        setLoading(true);
        const blob = await pdf(<Component {...componentProps} />).toBlob();

        let finalBlob = blob;

        if (pdfAttachments?.length) {
          finalBlob = await handleAttachments(blob, pdfAttachments);
        }

        return finalBlob;
      } catch (err) {
        logError(err);
        showError((err as any).message || "Ocorreu um erro ao exportar o PDF.");
      } finally {
        setLoading(false);
        setLoadingMessage(undefined);
      }
    },
    [Component, handleAttachments]
  );

  const onDownload = useCallback(
    async (
      updatedComponentProps: ComponentProps,
      pdfAttachments?: string[]
    ) => {
      try {
        setLoading(true);
        const blob = await onGetBlob(updatedComponentProps, pdfAttachments);
        if (blob) {
          downloadLocalBlob(
            blob,
            fileName.replace(".pdf", "") + ".pdf",
            "application/pdf"
          );
        }
      } catch (err) {
        logError(err);
        showError((err as any).message || "Ocorreu um erro ao exportar o PDF.");
      } finally {
        setLoading(false);
        setLoadingMessage(undefined);
      }
    },
    [fileName, onGetBlob]
  );

  useLoading(_loading || !!loading, loadingMessage ?? _loadingMessage);

  return {
    onDownload,
    onGetBlob,
  };
}

export default useDownloadRenderedPdf;
